import * as YAML from "yaml";

export interface ValidationError {
  line?: number;
  type: "error" | "warning";
  message: string;
  field?: string;
  color?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

const VALID_NODE_TYPES = ["Card", "Note", "Center", "Image", "MindMap"];

// Color palette for error highlighting
const ERROR_COLORS = [
  "#ffebee", // light red
  "#fff3e0", // light orange
  "#fff9c4", // light yellow
  "#f3e5f5", // light purple
  "#e8f5e9", // light green
  "#e1f5fe", // light cyan
  "#fce4ec", // light pink
  "#f1f8e9", // light lime
];

let colorIndex = 0;

export function validateTemplate(markdown: string): ValidationResult {
  colorIndex = 0; // Reset color index for each validation
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Check for H1 title
  const h1Match = markdown.match(/^#\s+(.+?)$/m);
  if (!h1Match) {
    errors.push({
      type: "error",
      message: "Template must have an H1 title (e.g., # My Template)",
      field: "title",
    });
  }

  // Check NodeType if present
  const nodeTypeMatch = markdown.match(/^NodeType:\s*(.+?)\s*$/m);
  if (nodeTypeMatch) {
    const nodeType = nodeTypeMatch[1].trim();
    if (!VALID_NODE_TYPES.includes(nodeType)) {
      errors.push({
        type: "error",
        message: `Invalid NodeType "${nodeType}". Must be one of: ${VALID_NODE_TYPES.join(", ")}`,
        field: "NodeType",
      });
    }
  } else {
    warnings.push({
      type: "warning",
      message: "No NodeType specified. Will default to 'Card'",
      field: "NodeType",
    });
  }

  // Validate YAML rules block if present
  const yamlMatch = markdown.match(/```yaml\n([\s\S]*?)\n```/);
  if (yamlMatch) {
    try {
      const yamlContent = yamlMatch[1];
      const parsed = YAML.parse(yamlContent);

      // Check for rulesVersion
      if (!parsed.rulesVersion) {
        warnings.push({
          type: "warning",
          message: "YAML rules block missing 'rulesVersion' field",
          field: "rulesVersion",
        });
      }

      // Validate roleInference structure
      if (parsed.roleInference) {
        if (parsed.roleInference.byLabelPrefix) {
          if (!Array.isArray(parsed.roleInference.byLabelPrefix)) {
            errors.push({
              type: "error",
              message: "roleInference.byLabelPrefix must be an array",
              field: "roleInference",
            });
          } else {
            parsed.roleInference.byLabelPrefix.forEach(
              (entry: any, index: number) => {
                if (!entry.prefix || !entry.role) {
                  errors.push({
                    type: "error",
                    message: `roleInference.byLabelPrefix[${index}] must have 'prefix' and 'role' fields`,
                    field: "roleInference",
                  });
                }
              },
            );
          }
        }
      }

      // Validate suggestNextChild structure
      if (parsed.suggestNextChild) {
        if (!Array.isArray(parsed.suggestNextChild)) {
          errors.push({
            type: "error",
            message: "suggestNextChild must be an array",
            field: "suggestNextChild",
          });
        } else {
          parsed.suggestNextChild.forEach((rule: any, index: number) => {
            if (!rule.when || !rule.when.parentRole) {
              errors.push({
                type: "error",
                message: `suggestNextChild[${index}] must have 'when.parentRole' field`,
                field: "suggestNextChild",
              });
            }
            if (!rule.suggest || !Array.isArray(rule.suggest)) {
              errors.push({
                type: "error",
                message: `suggestNextChild[${index}] must have 'suggest' array`,
                field: "suggestNextChild",
              });
            }
          });
        }
      }
    } catch (error) {
      errors.push({
        type: "error",
        message: `YAML parsing error: ${error instanceof Error ? error.message : String(error)}`,
        field: "yaml",
      });
    }
  }

  // Validate tree structure
  const treeStartMatch = markdown.match(/^\s*-\s+\w/m);
  if (treeStartMatch) {
    try {
      const treeValidation = parseTree(markdown);
      if (!treeValidation.root) {
        errors.push({
          type: "error",
          message:
            "Tree structure must have at least one root node at indentation level 0",
          field: "tree",
        });
      }
      if (treeValidation.errors.length > 0) {
        treeValidation.errors.forEach(
          (err: { line: number; message: string }) => {
            errors.push({
              line: err.line,
              type: "error",
              message: err.message,
              field: "tree",
              color: ERROR_COLORS[colorIndex++ % ERROR_COLORS.length],
            });
          },
        );
      }
    } catch (error) {
      errors.push({
        type: "error",
        message: `Tree structure parsing error: ${error instanceof Error ? error.message : String(error)}`,
        field: "tree",
      });
    }
  } else {
    warnings.push({
      type: "warning",
      message:
        "No tree structure found in template. Template will create an empty map.",
      field: "tree",
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

// Helper function to parse tree structure (same logic as in flow.tsx)
function parseTree(markdown: string): {
  root: any;
  errors: Array<{ line: number; message: string }>;
} {
  const lines = markdown.split("\n");
  let inTreeSection = false;
  let inCodeBlock = false;
  const stack: { node: any; indent: number; lineNum: number }[] = [];
  let root: any | null = null;
  const validationErrors: Array<{ line: number; message: string }> = [];
  const indentLevels = new Set<number>();

  // Helper function to calculate indent level (tabs count as 1 level, 2 spaces = 1 level)
  const calculateIndent = (whitespace: string): number => {
    let level = 0;
    for (let i = 0; i < whitespace.length; i++) {
      if (whitespace[i] === "\t") {
        level++;
      } else if (whitespace[i] === " ") {
        // Every 2 spaces = 1 level
        if (i + 1 < whitespace.length && whitespace[i + 1] === " ") {
          level++;
          i++; // Skip next space
        }
      }
    }
    return level;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Track code block boundaries (YAML blocks, etc.)
    if (line.trim().startsWith("```")) {
      inCodeBlock = !inCodeBlock;
      continue;
    }

    // Skip lines inside code blocks
    if (inCodeBlock) continue;

    // Start tree section when we find a bullet point outside code blocks
    if (!inTreeSection && line.match(/^\s*-\s+\w/)) {
      inTreeSection = true;
    }

    if (!inTreeSection) continue;

    const indentMatch = line.match(/^(\s*)-\s+(.+)/);
    if (!indentMatch) continue;

    const whitespace = indentMatch[1];
    const indent = calculateIndent(whitespace);
    const content = indentMatch[2].trim();

    // Skip Notes lines (don't track their indentation as it doesn't create nodes)
    if (content.startsWith("Notes:")) {
      if (stack.length > 0) {
        const notes = content.substring(6).trim();
        stack[stack.length - 1].node.notes = notes;
      } else {
        validationErrors.push({
          line: i + 1,
          message:
            "💡 This Notes line has no node to attach to. Add a node before it (e.g., '- My Node' on the line above).",
        });
      }
      continue;
    }

    // Track indent levels for actual nodes only (not Notes lines)
    indentLevels.add(indent);

    const newNode: any = {
      label: content,
      children: [],
      indent: indent,
    };

    if (indent === 0) {
      // Root node
      if (root !== null) {
        validationErrors.push({
          line: i + 1,
          message:
            "⚠️ Multiple root nodes found. Your template should have only ONE node without indentation (the main topic). All other nodes should be indented as children.",
        });
      }
      root = newNode;
      stack.length = 0;
      stack.push({ node: newNode, indent: 0, lineNum: i + 1 });
    } else {
      // Child node - must have a parent with less indentation
      while (stack.length > 0 && stack[stack.length - 1].indent >= indent) {
        stack.pop();
      }

      if (stack.length > 0) {
        stack[stack.length - 1].node.children.push(newNode);
        stack.push({ node: newNode, indent: indent, lineNum: i + 1 });
      } else {
        // Build a helpful error message
        const sortedIndents = Array.from(indentLevels).sort((a, b) => a - b);
        const expectedIndent =
          sortedIndents.find((level) => level < indent) || 0;
        const parentInfo =
          stack.length > 0
            ? ` The closest parent is on line ${stack[stack.length - 1].lineNum} with indent level ${stack[stack.length - 1].indent}.`
            : " There are no parent nodes available.";

        validationErrors.push({
          line: i + 1,
          message: `🔧 Indentation error: This line has indent level ${indent}. It needs a parent node with LESS indentation (like level ${expectedIndent}).${parentInfo}\n\n✅ Fix: Either reduce the indentation to level ${expectedIndent + 1} (use Tab or 2 spaces per level), or add a parent node above it with level ${expectedIndent}.`,
        });
      }
    }
  }

  if (!root && inTreeSection) {
    validationErrors.push({
      line: 0,
      message:
        "📝 Missing root node: Your tree needs to start with ONE node at the very beginning (no tabs/spaces before the dash).\n\nExample:\n- Main Topic\n\t- Subtopic 1 (use Tab key)\n\t- Subtopic 2",
    });
  }

  return { root, errors: validationErrors };
}
